import { awsOidcSteps } from "../aws/oidc";
import { customAuth } from "../standardAuthPatterns";
import { defineProduct, productVariant } from "../types";
import { domainToRegex } from "../urlPatternConversion";
import logo from "./logo.svg";

// Scoped to one cluster: the token is a presigned GetCallerIdentity URL naming a
// single cluster, so a wider pattern would hand it to clusters in other
// accounts. Lowercased because the proxy matches url::Url's serialization, which
// lowercases the host, while describe-cluster returns an uppercase cluster id.
const clusterUrlPattern = ({ clusterEndpoint }: Record<string, string>) =>
  domainToRegex(clusterEndpoint?.toLowerCase());

const clusterSteps = [
  {
    title: "Point 3B at the cluster",
    items: [
      {
        kind: "markdown",
        content:
          "Run `aws eks describe-cluster --name <cluster>` (or open the cluster in the [EKS console](https://console.aws.amazon.com/eks/home#/clusters)) and copy its **endpoint**.\n\nAn EKS API server presents a certificate from a private per-cluster CA rather than a public issuer, so 3B needs that CA to verify it. `describe-cluster` returns it base64-encoded, so decode it into a `.pem` file and upload that as the connector's **CA certificate**:\n\n```\naws eks describe-cluster --name <cluster> \\\n  --query cluster.certificateAuthority.data --output text \\\n  | base64 --decode > cluster-ca.pem\n```",
      },
      {
        kind: "baseUrl",
        label: "Cluster endpoint",
        placeholder: "https://ABC123.gr7.eu-west-1.eks.amazonaws.com",
        required: true,
        helperText: "The API server URL from `describe-cluster`.",
        target: { kind: "configuration", field: "clusterEndpoint" },
      },
      {
        kind: "input",
        label: "Cluster name",
        placeholder: "my-cluster",
        required: true,
        helperText:
          "Signed into every token, so it must match the cluster exactly.",
        target: { kind: "configuration", field: "clusterName" },
      },
      {
        kind: "input",
        label: "Region (optional)",
        placeholder: "eu-west-1",
        helperText:
          "Derived from the cluster endpoint. Set this only when the endpoint carries no region — a private endpoint or a CNAME — since tokens signed against the wrong region are rejected.",
        target: { kind: "configuration", field: "region" },
      },
    ],
  },
  {
    title: "Grant the identity access inside the cluster",
    items: [
      {
        kind: "markdown",
        content:
          "IAM decides *who* the caller is; Kubernetes decides what they may do. Map the identity to a cluster role, or `kubectl` will connect and then return `Unauthorized`.\n\nOn a cluster using [access entries](https://docs.aws.amazon.com/eks/latest/userguide/access-entries.html) (the default for newer clusters):\n\n```\naws eks create-access-entry --cluster-name <cluster> --principal-arn <arn>\naws eks associate-access-policy --cluster-name <cluster> --principal-arn <arn> \\\n  --access-scope type=cluster \\\n  --policy-arn arn:aws:eks::aws:cluster-access-policy/AmazonEKSViewPolicy\n```\n\nOn an older cluster, add the identity to the `aws-auth` ConfigMap instead. Pick the narrowest policy your workflows need — `AmazonEKSViewPolicy` for read-only, `AmazonEKSAdminPolicy` for write access.",
      },
    ],
  },
] as const;

const usageHint = () =>
  "kubectl, helm, and the Kubernetes client libraries work as normal — 3B mints the EKS bearer token on egress. Don’t run `aws eks get-token` or keep a `user.exec` block in the kubeconfig: the step holds no real AWS credentials and can’t sign a token. Point `certificate-authority` at the system CA bundle ($SSL_CERT_FILE) rather than the cluster CA, because 3B terminates TLS and presents its own certificate. `aws eks update-kubeconfig` is a fine starting point if you then delete the exec section. Only this cluster’s API server is authenticated by this connector.";

const publicEnvVars = ({
  clusterEndpoint,
  clusterName,
}: Record<string, string>) => ({
  KUBERNETES_CLUSTER_ENDPOINT: clusterEndpoint?.trim() ?? "",
  KUBERNETES_CLUSTER_NAME: clusterName?.trim() ?? "",
});

const connectionTest = {
  protocol: "http",
  request: ({ clusterEndpoint }: Record<string, string>) =>
    `${clusterEndpoint?.trim().replace(/\/+$/, "")}/version`,
} as const;

export const awsEks = defineProduct({
  name: "AWS EKS",
  tagLine: "Kubernetes clusters on Amazon EKS",
  logo,
  tags: ["it", "engineering"],
  variants: [
    productVariant({
      identifier: "awsEks",
      authLabel: "OIDC (IAM role federation)",
      protocol: "http",
      defaultUrlPattern: clusterUrlPattern,
      auth: (app) =>
        customAuth("awsEks", {
          clusterName: app.config.clusterName,
          clusterEndpoint: app.config.clusterEndpoint,
          region: app.config.region,
          roleArn: app.config.roleArn,
          issuer: app.issuer ?? "",
        }),
      steps: [...awsOidcSteps, ...clusterSteps],
      connectionTest,
      usageHint,
      publicEnvVars,
    }),
    productVariant({
      identifier: "awsEksKey",
      authLabel: "Key-based (access/secret key pair)",
      protocol: "http",
      defaultUrlPattern: clusterUrlPattern,
      auth: (app) =>
        customAuth("awsEks", {
          clusterName: app.config.clusterName,
          clusterEndpoint: app.config.clusterEndpoint,
          region: app.config.region,
          accessKeyId: app.config.accessKeyId,
          secretAccessKey: app.secret.secretAccessKey,
          sessionToken: { from: "secret", field: "sessionToken" },
        }),
      steps: [
        {
          title: "Enter your AWS credentials",
          items: [
            {
              kind: "markdown",
              content:
                "Use an IAM user’s access key. It needs no IAM permissions policy for EKS — the token it signs only identifies the user, and the cluster authorizes it in a later step.",
            },
            {
              kind: "input",
              label: "Access key ID",
              placeholder: "AKIA...",
              required: true,
              target: { kind: "configuration", field: "accessKeyId" },
            },
            {
              kind: "input",
              label: "Secret access key",
              placeholder: "Paste your secret access key",
              required: true,
              target: { kind: "secrets", field: "secretAccessKey" },
            },
          ],
        },
        ...clusterSteps,
      ],
      connectionTest,
      usageHint,
      publicEnvVars,
    }),
  ],
});
